/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Formik } from 'formik';
import toast from 'react-hot-toast';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useRecoilState, useRecoilValue } from 'recoil';

import { transformErrors, handleFetchErrors } from '@/services/apollo';
import Table from '@/components/commons/table';
import { SortDirection, toggleDirection } from '@/services/sorting';
import EmptyState from '@/components/commons/empty_state';
import ToggleForm from '@/components/forms/toggle_form';
import checkBoxStyles from '@/components/forms/checkbox/Checkbox.module.css';
import TrashBin from '@/components/commons/icons/TrashBin';
import ConfirmActionModal from '@/components/settings/ConfirmActionModal';

import Dropdown from '@/components/commons/dropdown';
import { DELETE_MAIL_RULE, UPDATE_MAIL_RULE } from './Actions';
import { selectedMailRules, mailRulesTablePageSizeOptions, mailRulesTableSelectedPageSize } from './State';
import { SELECT_TYPES } from './Constants';
import styles from './MailRules.module.css';

export const DEFAULT_SORT_COLUMN = 'senderEmail';
export const DEFAULT_SORT_DIRECTION = SortDirection.ASC;

function MailRulesTable({
    setLoading,
    refetch,
    mailRules,
    mailRulesHolder,
    sortColumn,
    sortDirection,
    setSortColumn,
    setSortDirection,
    formError,
    setFormError,
}) {
    const [allSelected, setAllSelected] = useState(false);
    const [pageSelected, setPageSelected] = useState(false);
    const [selectType, setSelectType] = useState(SELECT_TYPES[0].value);

    const [page, setPage] = useState(0);
    const [selectedRules, setSelectedRules] = useRecoilState(selectedMailRules);
    const [selectedSize, setSelectedSize] = useRecoilState(mailRulesTableSelectedPageSize);
    const pageSizeOptions = useRecoilValue(mailRulesTablePageSizeOptions);

    const [modalState, setModalState] = useState({ show: false, action: null });
    const [activeRecord, setActiveRecord] = useState(null);

    const [callToAction, setCallToAction] = useState('You don\'t have any rules yet.  Move messages into Sixty folders to create them.');
    const [pageName, setPageName] = useState('Rules');

    useEffect(() => {
        const callToActionValue = (mailRulesHolder && mailRulesHolder.length > 0)
            ? 'No rules were found that match your query.'
            : 'You don\'t have any rules yet.  Move messages into Sixty folders to create them.';

        const pageNameValue = (mailRulesHolder && mailRulesHolder.length > 0) ? 'rules found' : 'Rules';

        setCallToAction(callToActionValue);
        setPageName(pageNameValue);
    }, [mailRulesHolder, pageName]);

    const [deleteMaileRule] = useMutation(DELETE_MAIL_RULE, {
        onCompleted: () => {
            refetch();
            setLoading(false);
        },
    });
    const [updateMailRule] = useMutation(UPDATE_MAIL_RULE, {
        onCompleted: () => {
            refetch();
            setLoading(false);
        },
    });

    const handleUpdateMailRule = async (id, enabled, _) => {
        try {
            setLoading(true);
            const response = await updateMailRule({
                variables: {
                    updateMailRuleInput: {
                        id,
                        attributes: {
                            enabled,
                        },
                    },
                },
            });
            if (response.data.updateMailRule.mailRule) {
                if (formError !== '') setFormError('');
                toast.success('Mail Rule was updated successfully.');
            } else {
                const rawErrors = transformErrors(response.data.updateMailRule.errors)['input'] // eslint-disable-line dot-notation
                    .attributes.mailRule;
                const errMsgs = Object.entries(rawErrors).map((ent) => ent[1]);
                setFormError(`Mail Rule update failed: ${errMsgs.join(', \n')}`);
            }
        } catch (err) {
            setLoading(false);
            handleFetchErrors(err);
            setFormError('An unexpected error occurred.  Please try again later.');
        }
    };

    const handleDeleteMailRule = async (id) => {
        try {
            setLoading(true);
            const response = await deleteMaileRule({
                variables: {
                    deleteMailRuleInput: {
                        id,
                    },
                },
            });
            if (!response.errors) {
                if (formError !== '') setFormError('');
                toast.success('Mail Rule was removed successfully.');
            } else {
                const rawErrors = transformErrors(response.data.deleteMailRule.errors)['input'] // eslint-disable-line dot-notation
                    .attributes.mailRule;
                const errMsgs = Object.entries(rawErrors).map((ent) => ent[1]);
                setFormError(`Mail Rule removal failed: ${errMsgs.join(', \n')}`);
            }
        } catch (err) {
            setLoading(false);
            handleFetchErrors(err);
            setFormError('An unexpected error occurred.  Please try again later.');
        }
    };

    const handleColumnSort = (column, defaultDirection) => {
        if (sortColumn && sortColumn !== column) { setSortColumn(column); return; }

        const newDirection = sortColumn && sortColumn === column
            ? toggleDirection(sortDirection) : defaultDirection;

        setSortDirection(newDirection);
    };

    const updateSelectedRules = (checked: boolean, data: string) => {
        if (checked) {
            const uniqueIds = new Set(selectedRules);
            uniqueIds.add(data);
            setSelectedRules(Array.from(uniqueIds));
        } else {
            const arr = selectedRules.filter((rule) => rule !== data);
            setSelectedRules(arr);
        }
    };

    useEffect(() => {
        // any logice?
    }, [selectedRules]);

    const selectAll = () => {
        if (!allSelected) {
            const rulesIds: Array<string> = mailRules.map((rule) => rule.id);
            const uniqueIds = new Set(rulesIds);
            setSelectedRules(Array.from(uniqueIds));
            setAllSelected(true);
        } else {
            setSelectedRules([]);
            setAllSelected(false);
            setPageSelected(false);
        }
    };

    const selectPage = () => {
        if (!pageSelected) {
            const start = page * selectedSize;
            const rulesIds = mailRules.slice(start, start + selectedSize).map((rule) => rule.id);
            setSelectedRules(rulesIds);
            setPageSelected(true);
        } else {
            setSelectedRules([]);
            setPageSelected(false);
            setAllSelected(false);
        }
    };

    const handleSelection = (type: number) => {
        switch (type) {
        case 0: selectPage();
            return;
        case 1: selectAll();
            return;
        default: selectPage();
        }
    };

    const openDeleteMailRulesModal = async (recordId) => {
        setModalState({ show: true, action: null });
        setActiveRecord(recordId);
    };

    const confirmDeleteMailRule = async () => {
        handleDeleteMailRule(activeRecord);
        setActiveRecord(null);
        setModalState({ show: false, action: null });
    };

    const columns = [
        {
            label: () => (
                <Dropdown
                    onSelect={(value: number) => {
                        setSelectType(value);
                        handleSelection(value);
                    }}
                    selectionOptions={SELECT_TYPES}
                    withArrow
                    direction={0}
                    isAction={false}
                >
                    <input
                        type="checkbox"
                        name="selected-all"
                        className={`${checkBoxStyles.checkbox} ml-0 sm:ml-[12px]`}
                        checked={allSelected || pageSelected}
                        onChange={() => handleSelection(selectType)}
                    />
                </Dropdown>
            ),
            id: 'selection',
            render: (_rowId, record) => {
                const isSelected = selectedRules.includes(record.id);

                return (
                    <input
                        type="checkbox"
                        name={`selected-${record.id}`}
                        className={`${checkBoxStyles.checkbox} ml-0 sm:ml-[12px]`}
                        checked={isSelected}
                        onChange={(e) => updateSelectedRules(e.target.checked, record.id)}
                    />
                );
            },
        },
        {
            label: 'Sender',
            id: 'senderEmail',
            onSort: () => handleColumnSort('senderEmail', DEFAULT_SORT_DIRECTION),
            render: (rowId, record) => <div key={rowId}>{record.senderEmail}</div>,
        },
        {
            label: 'Folder',
            id: 'targetName',
            onSort: () => handleColumnSort('targetName', DEFAULT_SORT_DIRECTION),
            render: (rowId, record) => <div key={rowId}>{record.targetName}</div>,
        },
        {
            label: 'Mailbox',
            id: 'accountEmail',
            onSort: () => handleColumnSort('accountEmail', DEFAULT_SORT_DIRECTION),
            render: (rowId, record) => <div key={rowId}>{record.accountEmail}</div>,
        },
        {
            label: 'Enabled',
            id: 'enabled',
            render: (rowId, record) => {
                const fieldName = `enabled-${record.id}`;
                const initialValues = {};
                initialValues[fieldName] = record.enabled;
                return (
                    <div key={rowId}>
                        <Formik
                            onSubmit={() => { }}
                            initialValues={initialValues}
                        >
                            <ToggleForm
                                record={record}
                                onChange={handleUpdateMailRule}
                                fieldName={fieldName}
                                propertyName="enabled"
                            />
                        </Formik>
                    </div>
                );
            },
        },
        {
            label: '',
            id: 'deleteAction',
            render: (rowId, record) => (
                <div key={rowId} className="float-right pr-2">
                    <button
                        type="button"
                        data-tooltip-id="tip"
                        data-tooltip-content="Delete this Mail Rule"
                        title="Delete this Mail Rule"
                        aria-label="Delete this Mail Rule"
                        className={styles.trashButton}
                        onClick={() => openDeleteMailRulesModal(record.id)}
                    >
                        <TrashBin className="h-4 w-4 ml-3 mb-[2px] text-progressBarBlue" />
                    </button>
                </div>
            ),
        },
    ];

    return (
        mailRules && mailRules.length
            ? (
                <>
                    <Table
                        columns={columns}
                        data={mailRules}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        paginationState={{ windowSize: 3, initialPage: 0 }}
                        allowPageSizeOptions
                        setSelectedSize={setSelectedSize}
                        selectedSize={selectedSize}
                        pageSizeOptions={pageSizeOptions}
                        selectedRows={selectedRules}
                        setParentPage={setPage}
                    />
                    <ConfirmActionModal
                        modalType="agenda"
                        element="Mail Rule"
                        display={modalState.show}
                        onSubmit={() => confirmDeleteMailRule()}
                        closeModal={() => {
                            setActiveRecord(null);
                            setModalState({ show: false, action: null });
                        }}
                        actionType={modalState.action || ''}
                    />
                    <Tooltip id="tip" />
                </>
            ) : (
                <EmptyState
                    callToActionMessage={callToAction}
                    pageName={pageName}
                />
            )
    );
}

export default MailRulesTable;
